import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
  ThemeProvider,
  createTheme
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SubmitHandler, useForm } from "react-hook-form";
import { ChangeCustomerContact, CustomerContact } from "../../shared/models/customerContactsModels";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import { changeContact, createContact, getContacts } from "../customerContactsSlice";
import { emailRegex, phoneRegex, phoneRegexMessage } from "../../../shared/validators";
import UploadImage from "../../../shared/components/UploadImage";
import { FileModel } from "../../../shared/models/models";
import useTranslation from "../../../shared/hooks/useTranslation";

export interface ContactDialogProps {
  open: boolean;
  onClose: () => void;
  contact?: CustomerContact | null;
}

const ContactDialog = ({ open, onClose, contact }: ContactDialogProps) => {
  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ChangeCustomerContact>();
  const isNew: boolean = !contact;
  const { translate } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");

  const onSubmit: SubmitHandler<ChangeCustomerContact> = (data: ChangeCustomerContact) => {
    if (isNew) {
      handleCreateContact(data);
    } else {
      handleChangeContact(data);
    }
  };

  const [file, setFile] = useState<FileModel | null>(contact?.file || null);

  const handlerCreateFile = (file: FileModel | null) => {
    setFile(file);
  };

  const handleChangeContact = async (model: ChangeCustomerContact) => {
    model.avatar = file;
    if (contact) {
      await dispatch(changeContact({ id: contact.id, model }));
      await dispatch(getContacts());
      onClose();
    }
  };

  const handleCreateContact = async (model: ChangeCustomerContact) => {
    model.avatar = file;
    await dispatch(createContact(model));
    onClose();
    await dispatch(getContacts());
  };

  useEffect(() => {
    if (contact) {
      const changeContact: ChangeCustomerContact = {
        avatar: contact.file,
        name: contact.name,
        position: contact.position,
        department: contact.department,
        email: contact.email,
        phoneNumber: contact.phoneNumber
      };
      reset(changeContact);
    } else {
      const changeContact: ChangeCustomerContact = {
        avatar: file,
        name: "",
        position: "",
        department: "",
        email: "",
        phoneNumber: ""
      };
      reset(changeContact);
    }
  }, [contact, reset]);

  const theme = createTheme({
    components: {
      MuiDialog: {
        styleOverrides: {
          root: {
            ".MuiDialogContent-root": {
              padding: "0 24px 20px"
            },

            ".MuiDialogTitle-root": {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center"
            },

            ".MuiPaper-root": {
              width: "480px",
              display: "flex",
              justifyContent: "center"
            }
          }
        }
      }
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          <p className="heading-2">{isNew ? translate("createContact") : translate("editContact")}</p>
        </DialogTitle>

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 14,
            top: 14,
            color: "#333333"
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent>
          <form id="form" onSubmit={handleSubmit(onSubmit)}>
            <Box className="flex justify-center">
              <Box>
                <Box className="flex relative">
                  <UploadImage
                    file={file}
                    maxFileSizeMb={2}
                    onChangeFile={handlerCreateFile}
                    deleteButtonTitle={translate("deleteAvatar")}
                    cropVariant="circle"
                    minCropWidth={120}
                    modalWidth="396px"
                    aspectRatio={1}
                  />

                  <Box className="ms-6" sx={{ maxWidth: "280px" }}>
                    <Box className="text-16-bold" color={"#333333"}>
                      {translate("avatarRequirements")}
                    </Box>

                    <Box className="text-14-regular mt-2 ml-2" color={"#333333"}>
                      <ul className="ms-3" style={{ listStyle: "outside" }}>
                        <li>{translate("avatarSize")}</li>
                        <li>{translate("avatarFileType")}</li>
                        <li>{translate("avatarMaxSize")}</li>
                      </ul>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box className="mt-6">
              <InputLabel required>{tCommon("forms.name")}</InputLabel>
              <TextField
                className="w-full"
                {...register("name", { required: true })}
                error={!!errors.name}
                helperText={errors.name ? tCommon("forms.required") : ""}
              />
            </Box>

            <Box className="mt-6">
              <InputLabel>{translate("position")}</InputLabel>
              <TextField className="w-full" {...register("position")} />
            </Box>

            <Box className="mt-6">
              <InputLabel>{translate("department")}</InputLabel>
              <TextField className="w-full" {...register("department")} />
            </Box>

            <Box className="mt-6">
              <InputLabel>{tCommon("forms.email")}</InputLabel>
              <TextField
                className="w-full"
                {...register("email", { pattern: emailRegex, maxLength: 100 })}
                error={!!errors.email}
                helperText={errors.email ? translate("invalidEmail") : ""}
              />
            </Box>

            <Box className="mt-6">
              <InputLabel>{tCommon("forms.phone")}</InputLabel>
              <TextField
                className="w-full"
                {...register("phoneNumber", {
                  pattern: { value: phoneRegex, message: translate("account:invalidPhone") },
                  maxLength: 25
                })}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber ? errors.phoneNumber.message || tCommon("forms.invalidValue") : ""}
              />
            </Box>
          </form>
        </DialogContent>

        <DialogActions className="flex">
          <Button variant="text" className="w-full" onClick={onClose}>
            {tCommon("buttons.cancel")}
          </Button>

          <Button form="form" type="submit" variant="contained" className="w-full">
            {isNew ? tCommon("create") : tCommon("saveChanges")}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};
export default ContactDialog;